<template>
	<component :data="acfLink" :url="url || false" :title="title" :is="dynamicComponent" :class="customClasses">
		<span :txtClass="txtClass" class="txt" v-if="$slots && $slots.default"><slot></slot></span>
		<span
			aria-hidden="true"
			focusable="false"
			role="presentation"
			:class="iconClass"
			class="icon"
			v-if="iconSvg"
			v-html="iconSvg"
		></span>
	</component>
</template>

<script>
import icons from '@/icons'
import AcfLink from '@/components/elements/AcfLink'
import WpLink from '@/components/elements/WpLink'

export default {
	name: 'button-vue',
	components: {
		AcfLink,
	},
	props: {
		theme: {
			type: String,
			default: 'white',
		},
		accent: {
			type: String,
			default: '',
		},
		iconClass: {
			type: String,
			default: '',
		},
		iconPos: {
			type: String,
			default: 'right',
		},
		title: {
			type: String,
		},
		txtClass: {
			type: String,
			default: '',
		},
		url: {
			type: String,
			default: '',
		},
		acfLink: {
			type: Object,
		},
		icon: {
			type: String,
			default: '',
		},
		//Display button as standard button
		button: {
			type: Boolean,
			default: false,
		},
		block: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		customClasses() {
			let classList = []
			classList.push('button')

			if (this.type) classList.push(this.type)
			if (this.block) classList.push('block')
			if (this.link) classList.push('link')
			if (this.theme) classList.push('button--' + this.theme)
			if (this.theme === 'light' && this.accent) {
				classList.push('accent--' + this.accent)
			}
			if (this.icon) {
				classList.push('has-icon')
				classList.push('icon-' + this.iconPos)
				classList.push('icon-' + this.icon)
			}
			return classList
		},
		dynamicComponent() {
			const isAcfLink = this.acfLink && this.acfLink.url
			const isUrl = this.url && this.url
			return this.button ? 'button' : isAcfLink ? AcfLink : isUrl ? WpLink : 'a'
		},
		iconSvg() {
			return this.icon ? this.getIcon(this.icon) : false
		},
	},
	mounted() {
		const VM = this
	},
	methods: {
		getIcon(iconName, iconColor = '#222831') {
			const fn = icons[iconName]
			if (typeof icons[iconName] === 'function') {
				return fn(iconColor)
			} else {
				return iconName
			}
		},
	},
}
</script>

<style lang="scss">
@import '@/assets/vars.scss';
@import '@/assets/mixins.scss';
/* Stroke animation. Use ems to scale :) */

.button {
	border: none;
	box-shadow: none;
	color: $dark;
	cursor: pointer;
	position: relative;
	@include fluid-type($smallResText, $wideResText, 16px, 20px);
	font-weight: 400;
	padding: 10px 25px;
	position: relative;
	text-decoration: none;
	background-color: transparent;
	will-change: color, box-shadow;
	z-index: 1;
	border-radius: 50px;
	display: inline-flex;
	background-color: #fff;
	align-items: center;
	justify-content: space-between;
	transition: all 0.15s ease-in-out;
	font-weight: 600;
	min-height: 50px;
	span {
		display: inline-block;
		line-height: 1.1;
	}
	@include mquery($tablet) {
		min-height: 45px;
		padding: 8px 20px;
	}
	@include mquery($phone) {
		min-height: 40px;
		padding: 8px 17px;
	}
	&:hover {
		box-shadow: 0 0 0 2px var(--dark);
	}

	&.block {
		display: flex;
		width: 100%;
	}

	&.external {
		svg {
			transform: rotate(-45deg);
		}
	}

	&.has-icon {
		svg {
			width: 25px;
			max-height: 25px;
			@include mquery($phone) {
				width: 20px;
				max-height: 20px;
			}

			path {
				transition: fill 0.15s ease-in-out;
			}
		}
		.icon,
		svg {
			line-height: 0;
		}
		&.icon-right .icon {
			margin-left: 15px;
		}
		&.icon-left .icon {
			margin-right: 15px;
		}
	}

	&--dark,
	&--blue,
	&--green,
	&--purple,
	&--red {
		color: #fff;
		path {
			fill: #fff;
		}
		&:hover {
			background-color: #fff;
		}
	}
	&.accent {
		&--red {
			color: var(--red);
			path {
				fill: var(--red);
			}
		}
		&--blue {
			color: var(--blue);
			path {
				fill: var(--blue);
			}
		}
		&--purple {
			color: var(--purple);
			path {
				fill: var(--purple);
			}
		}
		&--green {
			color: var(--green);
			path {
				fill: var(--green);
			}
		}
	}
	&--light {
		&:hover {
			box-shadow: 0 0 0 2px #fff;
			background-color: transparent;
			color: #fff;
			path {
				fill: #fff;
			}
		}
	}

	&--dark {
		background-color: var(--dark);
		&:hover {
			box-shadow: 0 0 0 2px var(--dark);
			color: var(--dark);
			path {
				fill: var(--dark);
			}
		}
	}

	&--blue {
		background-color: var(--blue);
		&:hover {
			box-shadow: 0 0 0 2px var(--blue);
			color: var(--blue);
			path {
				fill: var(--blue);
			}
		}
	}

	&--red {
		background-color: var(--red);
		&:hover {
			color: var(--red);
			box-shadow: 0 0 0 2px var(--red);
			path {
				fill: var(--red);
			}
		}
	}

	&--purple {
		background-color: var(--purple);
		&:hover {
			color: var(--purple);
			box-shadow: 0 0 0 2px var(--purple);
			path {
				fill: var(--purple);
			}
		}
	}

	&--green {
		background-color: var(--green);
		&:hover {
			color: var(--green);
			box-shadow: 0 0 0 2px var(--green);
			path {
				fill: var(--green);
			}
		}
	}
}
</style>
